import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Button
} from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useState, useRef, useEffect } from 'react';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const DEBUG_IMG = false; // coloque false para silenciar os logs

// --- Helpers robustos para Google Drive + logs --- //
const extractDriveId = (url) => {
  if (!url) return null;
  // /file/d/{id}/view...
  const m1 = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (m1?.[1]) return m1[1];
  // open?id={id}
  const m2 = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (m2?.[1]) return m2[1];
  return null;
};

const toDriveImageUrl = (url, opts = { type: "view", width: 0 }) => {
  if (!url) return url;
  const id = extractDriveId(url);
  if (!id) {
    DEBUG_IMG && console.warn("[IMG] URL não parece ser do Drive, usando original:", url);
    return url;
  }
  if (opts.type === "thumb" && opts.width) {
    const u = `https://drive.google.com/thumbnail?id=${id}&sz=w${opts.width}`;
    DEBUG_IMG && console.log("[IMG] Drive thumbnail URL:", { original: url, id, out: u });
    return u;
  }
  const u = `https://drive.google.com/uc?export=view&id=${id}`;
  DEBUG_IMG && console.log("[IMG] Drive view URL:", { original: url, id, out: u });
  return u;
};
// ------------------------------------------------ //

const formatarData = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

const ImageCard = ({ imagem, onDelete, onEdit, readonly = false }) => {
  const primarySrc = toDriveImageUrl(imagem.url, { type: "view" });
  const isPending = Boolean(imagem.__placeholder);
  const [expanded, setExpanded] = useState(false);
  const [isDeleting,] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNome, setEditedNome] = useState(imagem.nome);
  const [editedDescricao, setEditedDescricao] = useState(imagem.descricao);
  const [imageLoading, setImageLoading] = useState(true);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const descricaoRef = useRef(null);
  const nomeRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [nomeError, setNomeError] = useState('');
  const [descricaoError, setDescricaoError] = useState('');
  const [displaySrc, setDisplaySrc] = useState(primarySrc);

  const triedFallbackRef = useRef(false);


  const handleDelete = () => {
    if (onDelete) {
      onDelete(imagem.id);
    }
  };

  const handleSave = async () => {
    // Validação de limites
    if (editedNome.length > 128) {
      setNomeError('O nome não pode ter mais de 128 caracteres');
      return;
    }
    if (editedDescricao.length > 256) {
      setDescricaoError('A descrição não pode ter mais de 256 caracteres');
      return;
    }

    try {
      setIsSaving(true);
      setNomeError('');
      setDescricaoError('');
      await onEdit(imagem.id, { nome: editedNome, descricao: editedDescricao });

      setIsEditing(false); // fecha o modo de edição
    } catch (e) {
      console.error('Falha ao salvar edição:', e);
      // (opcional) exibir toast
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (!isEditing) {
        let isOverflowing = false;

        // Verifica overflow do nome
        if (nomeRef.current) {
          const currentClamp = nomeRef.current.style.webkitLineClamp;
          nomeRef.current.style.webkitLineClamp = '2';
          isOverflowing = nomeRef.current.scrollHeight > nomeRef.current.clientHeight;
          nomeRef.current.style.webkitLineClamp = currentClamp;
        }

        // Verifica overflow da descrição
        if (descricaoRef.current && !isOverflowing) {
          const currentClamp = descricaoRef.current.style.webkitLineClamp;
          descricaoRef.current.style.webkitLineClamp = '3';
          isOverflowing = descricaoRef.current.scrollHeight > descricaoRef.current.clientHeight;
          descricaoRef.current.style.webkitLineClamp = currentClamp;
        }

        setShowExpandButton(isOverflowing);
      }
    };

    setTimeout(checkOverflow, 0);
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [imagem.nome, imagem.descricao, isEditing]);

  useEffect(() => {
    // Reset do fallback toda vez que a URL mudar
    triedFallbackRef.current = false;

    // Se FOR placeholder, NUNCA mostra loading
    if (imagem.__placeholder) {
      setImageLoading(false);
      return;
    }

    // Se NÃO for placeholder, não precisa de loading também
    // (a imagem já foi carregada no servidor)
    setImageLoading(false);
  }, [imagem.url, imagem.__placeholder]);

  useEffect(() => {
    setEditedNome(imagem.nome ?? '');
    setEditedDescricao(imagem.descricao ?? '');
  }, [imagem.nome, imagem.descricao]);

  useEffect(() => {
    // Se for placeholder, usa a URL local (blob)
    if (imagem.__placeholder) {
      setDisplaySrc(primarySrc);
      return;
    }

    // Se acabou de deixar de ser placeholder, pré-carrega a imagem do Drive
    if (imagem.__justUploaded && typeof primarySrc === 'string' && primarySrc.includes('drive.google.com')) {
      // Cria uma imagem temporária para pré-carregar
      const img = new Image();
      img.src = primarySrc;

      img.onload = () => {
        // Quando carregar, atualiza a src exibida
        setDisplaySrc(primarySrc);
      };

      img.onerror = () => {
        // Se falhar, tenta o fallback
        const id = extractDriveId(imagem.url);
        if (id) {
          const fallback = toDriveImageUrl(imagem.url, { type: "thumb", width: 1170 });
          const imgFallback = new Image();
          imgFallback.src = fallback;
          imgFallback.onload = () => setDisplaySrc(fallback);
          imgFallback.onerror = () => setDisplaySrc(primarySrc); // Usa a original mesmo se falhar
        } else {
          setDisplaySrc(primarySrc);
        }
      };
    } else {
      // Para imagens normais (não acabaram de ser enviadas)
      setDisplaySrc(primarySrc);
    }
  }, [imagem.__placeholder, imagem.__justUploaded, primarySrc, imagem.url]);


  return (
    <Grid
      item
      sx={{
        transition: 'all 0.3s ease',
        opacity: isDeleting ? 0 : 1,
        transform: isDeleting ? 'scale(0.8)' : 'scale(1)',
        width: { xs: '100%', sm: '50%', md: '31%' },
        flexBasis: { xs: '100%', sm: '50%', md: '31%' },
        maxWidth: { xs: '100%', sm: '50%', md: '31%' },
        margin: 'auto'
      }}
    >
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {imageLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
                zIndex: 1
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Badge sutil quando estiver pendente (opcional) */}
          {/* Badge quando estiver pendente (enviando) */}
          {isPending && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 2,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'rgba(255, 165, 0, 0.12)',
                color: '#ff8c00',
                fontSize: 12,
                fontWeight: 600
              }}
            >
              Enviando...
            </Box>
          )}

          {/* Badge de sucesso quando a imagem foi enviada */}
          {!isPending && imagem.__justUploaded && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'rgba(76, 175, 80, 0.12)',
                color: '#4caf50',
                fontSize: 12,
                fontWeight: 600
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 'bold'
                }}
              >
                ✓
              </Box>
              Enviado
            </Box>
          )}

          <CardMedia
            component="img"
            height="200"
            image={displaySrc}  // MUDE DE primarySrc PARA displaySrc
            alt={imagem.nome}
            onLoad={() => {
              // Não precisa fazer nada aqui
            }}
            onError={(e) => {
              // Só tenta fallback se não for blob (imagem do Drive que falhou)
              if (!e.target.src.startsWith('blob:')) {
                const id = extractDriveId(imagem.url);

                if (id && !triedFallbackRef.current) {
                  triedFallbackRef.current = true;
                  const fallback = toDriveImageUrl(imagem.url, { type: "thumb", width: 1170 });
                  e.target.src = fallback;
                  return;
                }

                const ph = "data:image/svg+xml;utf8," + encodeURIComponent(
                  `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='400'>
          <rect width='100%' height='100%' fill='#eee'/>
          <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#888' font-size='70'>
            imagem indisponível
          </text>
        </svg>`
                );
                e.target.onerror = null;
                e.target.src = ph;
              }
            }}
            referrerPolicy="no-referrer"
            sx={{ objectFit: 'cover', bgcolor: '#f5f5f5' }}
          />

          {/* Botões só quando não estiver pendente */}
          {!readonly && !isPending && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                gap: 1
              }}
            >
              <IconButton
                onClick={() => {
                  setIsEditing(!isEditing);
                  setEditedNome(imagem.nome);
                  setEditedDescricao(imagem.descricao);
                }}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 1)',
                    color: 'primary.main'
                  },
                  boxShadow: 1
                }}
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={handleDelete}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 1)',
                    color: 'error.main'
                  },
                  boxShadow: 1
                }}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>



        {DEBUG_IMG && (
          <Typography variant="caption" sx={{ color: '#999', wordBreak: 'break-all', mt: 0.5, px: 2 }}>
            src: {toDriveImageUrl(imagem.url, { type: "view" })}
          </Typography>
        )}

        <CardContent sx={{
          flexGrow: 1,
          overflow: 'auto', // ADICIONE ESTA LINHA
          display: 'flex',  // ADICIONE ESTA LINHA
          flexDirection: 'column' // ADICIONE ESTA LINHA
        }}>
          {isEditing ? (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ mb: 1 }}>
                <input
                  value={editedNome}
                  onChange={(e) => {
                    setEditedNome(e.target.value);
                    if (e.target.value.length <= 128) setNomeError('');
                  }}
                  placeholder="Nome da imagem"
                  disabled={isSaving}
                  style={{
                    width: '100%',
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginBottom: '4px',
                    padding: '8px 12px',
                    border: `2px solid ${editedNome.length > 128 ? '#d32f2f' : '#1976d2'}`,
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: editedNome.length > 128 ? '#d32f2f' : '#666',
                    fontSize: '0.75rem',
                    display: 'block',
                    mb: 1
                  }}
                >
                  {editedNome.length}/128 caracteres
                </Typography>
                {nomeError && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mb: 1 }}>
                    {nomeError}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 1 }}>
                <textarea
                  value={editedDescricao}
                  onChange={(e) => {
                    setEditedDescricao(e.target.value);
                    if (e.target.value.length <= 256) setDescricaoError('');
                  }}
                  placeholder="Descrição"
                  rows={3}
                  disabled={isSaving}
                  style={{
                    width: '100%',
                    fontSize: '0.875rem',
                    marginBottom: '4px',
                    padding: '8px 12px',
                    border: `2px solid ${editedDescricao.length > 256 ? '#d32f2f' : '#1976d2'}`,
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: editedDescricao.length > 256 ? '#d32f2f' : '#666',
                    fontSize: '0.75rem',
                    display: 'block',
                    mb: 1
                  }}
                >
                  {editedDescricao.length}/256 caracteres
                </Typography>
                {descricaoError && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mb: 1 }}>
                    {descricaoError}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {!isSaving && (
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditedNome(imagem.nome);
                      setEditedDescricao(imagem.descricao);
                    }}
                    style={{
                      padding: '6px 16px',
                      fontSize: '0.875rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={
                    isSaving ||
                    !editedNome.trim() ||
                    !editedDescricao.trim() ||
                    editedNome.length > 128 ||
                    editedDescricao.length > 256
                  }
                  sx={{
                    background: (isSaving || !editedNome.trim() || !editedDescricao.trim()) ? '#545454ff' : '#1976d2',
                    color: 'white',
                    '&.Mui-disabled': {
                      color: 'white',
                    }
                  }}
                >
                  {isSaving ? 'Salvando…' : 'Salvar'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography
              variant="h6"
              ref={nomeRef}
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                mb: 1,
                color: 'text.primary',
                display: '-webkit-box',
                WebkitLineClamp: expanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                hyphens: 'auto'
              }}
            >
              {imagem.nome}
            </Typography>
          )}

          {!isEditing && (
            <Typography
              ref={descricaoRef}
              variant="body2"
              sx={{
                color: '#666',
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: expanded ? 'unset' : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                hyphens: 'auto',
              }}
            >
              {imagem.descricao}
            </Typography>
          )}


          {showExpandButton && !isEditing && (
            <Typography
              variant="caption"
              onClick={() => setExpanded(!expanded)}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                display: 'inline-block',
                '&:hover': { textDecoration: 'underline' },
                mb: 1
              }}
            >
              {expanded ? 'Ver menos' : 'Ver mais'}
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pt: 1,
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: '#999' }} />
            <Typography variant="caption" sx={{ color: '#999' }}>
              {formatarData(imagem.dataUpload)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ImageCard;